import type { FC, PropsWithChildren } from "react";
import React from "react";

import { useFeatureState } from "../AbFeatures/FeaturesProvider";

type ComponentFactory<TProps> = Array<FC<TProps> | null>;

const EmptyComponent: FC = () => null;

function createComponent<TProps>(
  componentFactory: ComponentFactory<TProps> | ((index: number) => FC<TProps>),
  index: number,
): FC<TProps> | FC {
  if (typeof componentFactory === "function") {
    return componentFactory(index) || EmptyComponent;
  }

  if (!Array.isArray(componentFactory)) {
    return EmptyComponent;
  }

  const parsedPartition =
    !index || componentFactory.length < index ? 0 : index - 1;
  return componentFactory[parsedPartition] || EmptyComponent;
}

function useVersionedComponent<TProps>(
  alias: string,
  componentFactory: ComponentFactory<TProps>,
): FC<TProps> | FC {
  const { status, index } = useFeatureState(alias);

  const defaultComponent = componentFactory[0] || EmptyComponent;

  if (status === "success" && typeof index === "number") {
    return createComponent<TProps>(componentFactory, index);
  }

  return defaultComponent;
}

export const versionedComponent = (alias: string) =>
  function getVersionedComponent<TProps>(
    componentFactory: ComponentFactory<TProps>,
  ) {
    return (props: PropsWithChildren<TProps>) => {
      const Component = useVersionedComponent<TProps>(alias, componentFactory);
      return <Component {...props} />;
    };
  };

export const joinModuleABTestAliases = (moduleABTestAliases: string[][]) => {
  const activeAliases = moduleABTestAliases?.reduce(
    (activeAliases, aliases) => {
      aliases.forEach((alias) => activeAliases.add(alias));
      return activeAliases;
    },
    new Set<string>(),
  );
  return [...activeAliases];
};
